// === LIAR'S DICE (Le Jeu des Menteurs) ===
// Sprint 79 (Phase 7) — Logique pure du mini-jeu de la Taverne.
// Aucune dépendance Phaser : testable directement.
//
// Règles :
// - Chaque joueur lance 5 dés cachés au début de chaque manche
// - Tour de table : annoncer "X dés montrent face Y" (ex: "trois 4")
// - Le joueur suivant doit soit surenchérir (X+ dés OU Y+, jamais en arrière),
//   soit dire "menteur"
// - "Menteur" → on révèle. Si l'enchère est tenue (X dés ≥ annonce sur la face),
//   le contestataire perd 1 dé. Sinon, l'annonceur perd 1 dé.
// - Joueur éliminé à 0 dé. Dernier debout gagne la mise.
// - **Variante "wild ones"** : les 1 sont des JOKERS et comptent pour n'importe
//   quelle face (sauf si la face cherchée est 1 elle-même — alors on compte
//   strictement les 1).

export type DiceFace = 1 | 2 | 3 | 4 | 5 | 6;

/** État d'un joueur dans la partie. */
export interface DicePlayer {
  id: string;
  name: string;
  /** Dés en main (révélés ou pas). 0 dé = éliminé. */
  dice: DiceFace[];
  /** True si humain (joueur), false pour PNJ. */
  isHuman: boolean;
}

/** Une enchère : "X dés montrent face Y" */
export interface Bid {
  count: number;
  face: DiceFace;
  /** ID du joueur qui a fait l'enchère */
  playerId: string;
}

/** État global de la partie. */
export interface GameState {
  players: DicePlayer[];
  /** Index du joueur dont c'est le tour. */
  currentPlayerIdx: number;
  /** Dernière enchère active (null = début de manche). */
  currentBid: Bid | null;
  /** Manche en cours (incrémentée à chaque résolution menteur). */
  round: number;
  /** Phase : bidding, revealed (pendant qu'on montre les dés), finished. */
  phase: 'bidding' | 'revealed' | 'finished';
  /** Résultat du dernier appel "menteur" / "proposition juste" (pour l'UI). */
  lastReveal?: {
    callerId: string;
    bid: Bid;
    actualCount: number;
    /** Pour "menteur" : true si bid tenu (≥ count). Pour "proposition juste" : true si exact. */
    bidHeld: boolean;
    loserId: string;
    /** v0.38.14 — distingue "menteur" (false par défaut) de "proposition juste" (true).
     *  Utilisé par l'UI pour afficher le bon message + "+1 dé" sur succès. */
    wasExactCall?: boolean;
    /** True si le caller a GAGNÉ un dé (succès "proposition juste" uniquement). */
    callerWonDie?: boolean;
  };
  /** Cap maximum de dés par joueur (pour bonus "proposition juste"). */
  maxDicePerPlayer: number;
}

/** Lance 1 dé (1-6). */
export function rollDice(rng: () => number = Math.random): DiceFace {
  return (Math.floor(rng() * 6) + 1) as DiceFace;
}

/** Lance N dés. */
export function rollHand(count: number, rng: () => number = Math.random): DiceFace[] {
  const hand: DiceFace[] = [];
  for (let i = 0; i < count; i++) hand.push(rollDice(rng));
  return hand;
}

/**
 * Compte le nombre de dés montrant `face` parmi tous les joueurs vivants.
 * Variante "wild ones" : les 1 comptent comme jokers pour toutes les faces,
 * sauf si la face cherchée est 1 elle-même (alors on compte strictement les 1).
 */
export function countMatchingDice(players: DicePlayer[], face: DiceFace): number {
  let count = 0;
  for (const p of players) {
    for (const d of p.dice) {
      if (d === face) count++;
      else if (d === 1 && face !== 1) count++; // joker
    }
  }
  return count;
}

/** Détail par joueur : combien de vrais dés `face` et combien de jokers (1s) il a. */
export interface PlayerBreakdown {
  playerId: string;
  playerName: string;
  reals: number;   // dés montrant exactement `face`
  jokers: number;  // dés montrant 1 (compte comme joker, sauf si face=1)
}

/**
 * Décompose le comptage par joueur pour expliquer la résolution du menteur.
 * Si face=1, jokers vaut 0 partout (pas de joker possible).
 */
export function breakdownMatching(players: DicePlayer[], face: DiceFace): PlayerBreakdown[] {
  return players.map(p => {
    const reals = p.dice.filter(d => d === face).length;
    const jokers = face === 1 ? 0 : p.dice.filter(d => d === 1).length;
    return { playerId: p.id, playerName: p.name, reals, jokers };
  });
}

/**
 * Vérifie qu'une nouvelle enchère est strictement supérieure à la précédente.
 * Règles : count + > précédent, OU count = précédent + face >.
 */
export function isValidRaise(previous: Bid | null, next: Omit<Bid, 'playerId'>): boolean {
  if (!previous) return next.count >= 1 && next.face >= 1 && next.face <= 6;
  if (next.count > previous.count) return true;
  if (next.count === previous.count && next.face > previous.face) return true;
  return false;
}

/**
 * Initialise une nouvelle partie : N joueurs, chacun avec `diceCount` dés.
 * Le premier joueur est tiré au hasard.
 */
export function initGame(
  players: Array<{ id: string; name: string; isHuman: boolean }>,
  diceCount: number = 5,
  rng: () => number = Math.random,
): GameState {
  const dicePlayers: DicePlayer[] = players.map(p => ({
    ...p,
    dice: rollHand(diceCount, rng),
  }));
  return {
    players: dicePlayers,
    currentPlayerIdx: Math.floor(rng() * dicePlayers.length),
    currentBid: null,
    round: 1,
    phase: 'bidding',
    maxDicePerPlayer: diceCount,
  };
}

/**
 * Trouve l'index du prochain joueur vivant (>0 dés) après `currentIdx`.
 * Retourne -1 si personne n'est vivant (= partie finie).
 */
export function nextAlivePlayer(state: GameState, currentIdx: number): number {
  const n = state.players.length;
  for (let i = 1; i <= n; i++) {
    const idx = (currentIdx + i) % n;
    if (state.players[idx].dice.length > 0) return idx;
  }
  return -1;
}

/**
 * Joue une enchère depuis le joueur courant. Modifie l'état (mutation).
 * Renvoie le nouvel état.
 */
export function placeBid(state: GameState, bid: Omit<Bid, 'playerId'>): GameState {
  if (state.phase !== 'bidding') return state;
  const current = state.players[state.currentPlayerIdx];
  if (!isValidRaise(state.currentBid, bid)) return state;
  state.currentBid = { ...bid, playerId: current.id };
  state.currentPlayerIdx = nextAlivePlayer(state, state.currentPlayerIdx);
  return state;
}

/**
 * Le joueur courant accuse l'annonceur précédent de mensonge.
 * Révèle les dés et applique la pénalité (-1 dé au perdant).
 * IMPORTANT (Bug fix v0.36.3) : ne re-roll PAS les dés ici — c'est fait dans
 * continueAfterReveal. Sinon l'UI afficherait des dés incorrects pendant la
 * phase 'revealed' (les vrais dés qui ont servi à la vérification disparaitraient).
 */
export function callLiar(state: GameState, _rng: () => number = Math.random): GameState {
  if (state.phase !== 'bidding' || !state.currentBid) return state;
  const caller = state.players[state.currentPlayerIdx];
  const bid = state.currentBid;
  const annoncer = state.players.find(p => p.id === bid.playerId);
  if (!annoncer) return state;

  // Compter le total réel (face cible + tous les 1 sauf si la cible est 1)
  const actualCount = countMatchingDice(state.players, bid.face);
  const bidHeld = actualCount >= bid.count;
  // Si bid tenu (assez de dés trouvés) → caller perd 1 dé. Sinon → annoncer perd 1 dé.
  const loser = bidHeld ? caller : annoncer;
  loser.dice = loser.dice.slice(0, Math.max(0, loser.dice.length - 1));

  state.lastReveal = {
    callerId: caller.id,
    bid,
    actualCount,
    bidHeld,
    loserId: loser.id,
  };

  // Vérifier si la partie est finie (1 seul joueur avec >0 dés)
  const aliveCount = state.players.filter(p => p.dice.length > 0).length;
  if (aliveCount <= 1) {
    state.phase = 'finished';
    return state;
  }

  state.phase = 'revealed';
  // NB : currentBid est conservé pendant 'revealed' pour que l'UI puisse afficher l'enchère
  // qui a été contestée. Le re-roll + reset du bid arrivent dans continueAfterReveal.
  return state;
}

/**
 * Le joueur courant déclare "Proposition juste" (variante "Spot On" / "Calza").
 * Si le total réel == count exact de l'enchère → caller GAGNE 1 dé (cap à maxDicePerPlayer).
 * Sinon → caller PERD 1 dé.
 * Variante optionnelle classique du Liar's Dice — ajoute une couche de bluff
 * inverse (annoncer pile = autre joueur va sous-bid).
 */
export function callExact(state: GameState, _rng: () => number = Math.random): GameState {
  if (state.phase !== 'bidding' || !state.currentBid) return state;
  const caller = state.players[state.currentPlayerIdx];
  const bid = state.currentBid;
  const annoncer = state.players.find(p => p.id === bid.playerId);
  if (!annoncer) return state;

  const actualCount = countMatchingDice(state.players, bid.face);
  const isExact = actualCount === bid.count;

  let callerWonDie = false;
  let loserId = caller.id;

  if (isExact) {
    // Bonus : caller récupère 1 dé (cap au max original)
    if (caller.dice.length < state.maxDicePerPlayer) {
      // On ajoute un dé "placeholder" (face 1) ; sera re-roll dans continueAfterReveal
      caller.dice = [...caller.dice, 1 as DiceFace];
      callerWonDie = true;
    }
    // Personne n'est explicitement perdant → on met l'annonceur comme loserId
    // pour la cohérence (mais il ne perd PAS de dé). lastReveal.bidHeld=true encode
    // le succès, et callerWonDie=true le différencie d'un appel "menteur" tenu.
    loserId = annoncer.id;
  } else {
    // Échec : caller perd 1 dé
    caller.dice = caller.dice.slice(0, Math.max(0, caller.dice.length - 1));
    loserId = caller.id;
  }

  state.lastReveal = {
    callerId: caller.id,
    bid,
    actualCount,
    bidHeld: isExact,
    loserId,
    wasExactCall: true,
    callerWonDie,
  };

  // Vérifier fin de partie (1 seul joueur vivant)
  const aliveCount = state.players.filter(p => p.dice.length > 0).length;
  if (aliveCount <= 1) {
    state.phase = 'finished';
    return state;
  }

  state.phase = 'revealed';
  return state;
}

/**
 * Confirme la fin de la phase révélation : re-roll les dés des joueurs vivants,
 * reset l'enchère, prépare la manche suivante.
 * Appelée par l'UI après que le joueur a vu les résultats.
 */
export function continueAfterReveal(state: GameState, rng: () => number = Math.random): GameState {
  if (state.phase !== 'revealed' || !state.lastReveal) return state;
  // Re-roll des dés des joueurs encore en vie (avec leur nouveau dice count)
  for (const p of state.players) {
    if (p.dice.length > 0) p.dice = rollHand(p.dice.length, rng);
  }
  // Préparer la nouvelle manche
  state.currentBid = null;
  state.round++;
  state.phase = 'bidding';
  // Le perdant ouvre la prochaine manche (s'il est encore vivant)
  const loserIdx = state.players.findIndex(p => p.id === state.lastReveal!.loserId);
  if (loserIdx >= 0 && state.players[loserIdx].dice.length > 0) {
    state.currentPlayerIdx = loserIdx;
  } else {
    const nextIdx = nextAlivePlayer(state, loserIdx >= 0 ? loserIdx : 0);
    state.currentPlayerIdx = nextIdx >= 0 ? nextIdx : 0;
  }
  return state;
}

/** Renvoie l'ID du gagnant si la partie est finie, null sinon. */
export function getWinnerId(state: GameState): string | null {
  if (state.phase !== 'finished') return null;
  const survivor = state.players.find(p => p.dice.length > 0);
  return survivor?.id ?? null;
}

// ============================================================
// IA simple pour les PNJ
// ============================================================

export type AiProfile = 'cautious' | 'balanced' | 'aggressive';

/**
 * Décide de l'action d'un PNJ : enchérir ou crier "menteur".
 * Heuristique basique :
 * - Estime le total probable de la face annoncée (hand visible + estimation des autres)
 * - Si la dernière enchère est trop élevée vs estimation → call liar
 * - Sinon enchérit prudemment
 */
export function aiDecide(
  state: GameState,
  profile: AiProfile = 'balanced',
  rng: () => number = Math.random,
): { action: 'bid'; bid: Omit<Bid, 'playerId'> } | { action: 'call' } | { action: 'exact' } {
  const me = state.players[state.currentPlayerIdx];
  const totalDiceInGame = state.players.reduce((sum, p) => sum + p.dice.length, 0);
  const otherDiceCount = totalDiceInGame - me.dice.length;

  // Si pas d'enchère en cours, on doit ouvrir
  if (!state.currentBid) {
    // Compter notre face la plus représentée (en comptant les 1 comme jokers)
    const counts = new Array(7).fill(0); // index 1-6
    for (const d of me.dice) {
      counts[d]++;
      if (d === 1) for (let f = 2; f <= 6; f++) counts[f]++;
    }
    let bestFace: DiceFace = 2;
    let bestCount = counts[2];
    for (let f = 2; f <= 6; f++) {
      if (counts[f] > bestCount) { bestCount = counts[f]; bestFace = f as DiceFace; }
    }
    // Prudent : on annonce ce qu'on a en main, sans bluff (sauf agressif)
    const opening = profile === 'aggressive' ? bestCount + 1 : bestCount;
    return { action: 'bid', bid: { count: Math.max(1, opening), face: bestFace } };
  }

  // Estimer combien il y a de la face annoncée parmi les dés cachés
  const myMatching = me.dice.filter(d => d === state.currentBid!.face || d === 1).length;
  // Espérance : 1/3 des dés des autres (face cible OU 1 comme joker) sauf si face = 1 → 1/6
  const expectedFromOthers = otherDiceCount * (state.currentBid.face === 1 ? 1 / 6 : 1 / 3);
  const expectedTotal = myMatching + expectedFromOthers;

  // Marge selon profil (cautious appelle plus tôt, aggressive plus tard)
  const callMargin = profile === 'cautious' ? 0.5 : profile === 'aggressive' ? 2.0 : 1.0;
  if (state.currentBid.count > expectedTotal + callMargin) {
    return { action: 'call' };
  }

  // "Proposition juste" — variante Spot On : tente seulement si la prédiction est
  // EXACTEMENT à count, et seulement si l'IA n'est pas full dés (sinon pas de gain).
  // Risque/reward défavorable → faible probabilité même si l'estimation match.
  // Fenêtre serrée (±0.3) car l'estimation est probabiliste, pas déterministe.
  const exactProb = profile === 'cautious' ? 0.05 : profile === 'aggressive' ? 0.25 : 0.12;
  const matchExpected = Math.abs(expectedTotal - state.currentBid.count) < 0.3;
  if (matchExpected && me.dice.length < state.maxDicePerPlayer && rng() < exactProb) {
    return { action: 'exact' };
  }

  // Sinon on surenchérit minimal
  let nextCount = state.currentBid.count;
  let nextFace: DiceFace = state.currentBid.face;
  // Décider si on monte la face ou le count
  const bumpFace = rng() < 0.4 && state.currentBid.face < 6;
  if (bumpFace) {
    nextFace = (state.currentBid.face + 1) as DiceFace;
  } else {
    nextCount += 1;
  }
  return { action: 'bid', bid: { count: nextCount, face: nextFace } };
}
